import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "@/components/feature/AdminLayout";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import { logActivity } from "@/lib/logActivity";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import LeadModal, { type Lead } from "./components/LeadModal";
import LeadActionsModal, { type ActionMode, type ConvertOpts, type JobOpts, type ContractOpts } from "./components/LeadActionsModal";
import LeadsDashboard, { sourceCfg } from "./components/LeadsDashboard";
import LeadsFilters, { type AdvancedFilters } from "./components/LeadsFilters";

/* ─── Status config ──────────────────────────────────────────── */
const STATUSES = [
  { value: "new",       label: "New",       dot: "bg-sky-400",     badge: "bg-sky-400/15 text-sky-400 border-sky-400/25" },
  { value: "contacted", label: "Contacted", dot: "bg-amber-400",   badge: "bg-amber-400/15 text-amber-400 border-amber-400/25" },
  { value: "booked",    label: "Booked",    dot: "bg-emerald-400", badge: "bg-emerald-400/15 text-emerald-400 border-emerald-400/25" },
  { value: "lost",      label: "Lost",      dot: "bg-red-400",     badge: "bg-red-400/15 text-red-400 border-red-400/25" },
];

const statusCfg = (s: string) =>
  STATUSES.find((x) => x.value === s) ?? { value: s, label: s, dot: "bg-slate-300", badge: "bg-slate-100 text-slate-400 border-slate-200" };

const fmtDate = (d: string | null) => {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const DEFAULT_STEPS = [
  { step_key: "contract_deposit", title: "Contract / Deposit", sort_order: 0 },
  { step_key: "shoot_scheduled",  title: "Shoot Scheduled",    sort_order: 1 },
  { step_key: "shoot_completed",  title: "Shoot Completed",    sort_order: 2 },
  { step_key: "editing",          title: "Editing",            sort_order: 3 },
  { step_key: "client_selection", title: "Client Selection",   sort_order: 4 },
  { step_key: "final_delivery",   title: "Final Delivery",     sort_order: 5 },
  { step_key: "completed",        title: "Completed",          sort_order: 6 },
];

const EMPTY_FILTERS: AdvancedFilters = { source: "", event_type: "", date_from: "", date_to: "" };

/* ─── Page ───────────────────────────────────────────────────── */
export default function AdminLeadsPage() {
  const navigate = useNavigate();
  const [tenantId, setTenantId]     = useState<string | null>(null);
  const [leads, setLeads]           = useState<Lead[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search, setSearch]         = useState("");
  const [advFilters, setAdvFilters] = useState<AdvancedFilters>(EMPTY_FILTERS);
  const [showDashboard, setShowDashboard] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editLead, setEditLead]     = useState<Lead | null>(null);
  const [actionLead, setActionLead] = useState<Lead | null>(null);
  const [actionMode, setActionMode] = useState<ActionMode>("convert");

  /* ── Fetch ── */
  const fetchLeads = useCallback(async (tid: string) => {
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .from("tenant_public_leads")
        .select("*")
        .eq("tenant_id", tid)
        .order("created_at", { ascending: false });
      if (fetchErr) throw new Error(fetchErr.message);
      setLeads((data as Lead[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load leads.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchLeads(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchLeads]);

  /* ── Counts per status ── */
  const counts = useMemo(() => {
    const map: Record<string, number> = { all: leads.length };
    STATUSES.forEach((s) => { map[s.value] = leads.filter((l) => l.status === s.value).length; });
    return map;
  }, [leads]);

  /* ── Advanced filter active count ── */
  const advActiveCount = useMemo(() =>
    [advFilters.source, advFilters.event_type, advFilters.date_from, advFilters.date_to]
      .filter(Boolean).length,
    [advFilters],
  );

  /* ── Filtered ── */
  const filtered = useMemo(() => {
    let list = statusFilter === "all" ? leads : leads.filter((l) => l.status === statusFilter);

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((l) =>
        l.name.toLowerCase().includes(q) ||
        (l.email ?? "").toLowerCase().includes(q) ||
        (l.phone ?? "").toLowerCase().includes(q) ||
        (l.event_type ?? "").toLowerCase().includes(q),
      );
    }

    // Source
    if (advFilters.source) {
      list = list.filter((l) => (l.source ?? "manual") === advFilters.source);
    }

    // Event type
    if (advFilters.event_type) {
      list = list.filter((l) => l.event_type === advFilters.event_type);
    }

    // Date range
    if (advFilters.date_from) {
      const from = new Date(advFilters.date_from).getTime();
      list = list.filter((l) => new Date(l.created_at).getTime() >= from);
    }
    if (advFilters.date_to) {
      const to = new Date(advFilters.date_to + "T23:59:59").getTime();
      list = list.filter((l) => new Date(l.created_at).getTime() <= to);
    }

    return list;
  }, [leads, statusFilter, search, advFilters]);

  /* ── Inline status change ── */
  const handleStatus = async (id: string, status: string) => {
    if (!tenantId) return;
    setUpdatingId(id);
    await supabase.from("tenant_public_leads").update({ status }).eq("id", id).eq("tenant_id", tenantId);
    setLeads((prev) => prev.map((l) => l.id === id ? { ...l, status } : l));
    setUpdatingId(null);
  };

  /* ── Create manual lead ── */
  const handleCreateManual = async (data: Partial<Lead>) => {
    if (!tenantId) return;
    const { data: created, error } = await supabase
      .from("tenant_public_leads")
      .insert({
        tenant_id:  tenantId,
        name:       data.name,
        email:      data.email      ?? null,
        phone:      data.phone      ?? null,
        event_type: data.event_type ?? null,
        event_date: data.event_date ?? null,
        message:    data.message    ?? null,
        status:     data.status     ?? "new",
        source:     "manual",
      })
      .select("*")
      .maybeSingle();
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
      return;
    }
    if (created) setLeads((prev) => [created as Lead, ...prev]);
    setCreateOpen(false);
    Swal.fire({ title: "Lead added!", text: `"${data.name}" was registered as a manual lead.`, icon: "success", timer: 1800, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
    logActivity({ tenantId, action: "created", entity: "lead", entityId: (created as Lead).id, entityName: data.name ?? "" });
  };

  /* ── Edit lead ── */
  const handleSaveEdit = async (data: Partial<Lead>) => {
    if (!tenantId || !editLead) return;
    const { error } = await supabase
      .from("tenant_public_leads")
      .update(data)
      .eq("id", editLead.id)
      .eq("tenant_id", tenantId);
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
      return;
    }
    setLeads((prev) => prev.map((l) => l.id === editLead.id ? { ...l, ...data } : l));
    setEditLead(null);
    Swal.fire({ title: "Saved!", icon: "success", timer: 1200, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
  };

  /* ── Delete lead ── */
  const handleDelete = async (lead: Lead) => {
    if (!tenantId) return;
    const result = await Swal.fire({
      title: "Delete lead?",
      text: `"${lead.name}" will be permanently removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      background: "#ffffff", color: "#0f172a",
      confirmButtonColor: "#ef4444", cancelButtonColor: "#64748b",
    });
    if (!result.isConfirmed) return;
    await supabase.from("tenant_public_leads").delete().eq("id", lead.id).eq("tenant_id", tenantId);
    setLeads((prev) => prev.filter((l) => l.id !== lead.id));
    Swal.fire({ title: "Deleted", icon: "success", timer: 1200, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
  };

  const openAction = (lead: Lead, mode: ActionMode) => { setActionLead(lead); setActionMode(mode); };

  /* ── ensureClient ── */
  const ensureClient = async (lead: Lead, skipIfExists: boolean): Promise<string | null> => {
    if (!tenantId) return null;
    if (skipIfExists && (lead.email || lead.phone)) {
      if (lead.email) {
        const { data } = await supabase.from("clients").select("id").eq("tenant_id", tenantId).eq("email", lead.email).maybeSingle();
        if (data) return (data as { id: string }).id;
      }
      if (lead.phone) {
        const { data } = await supabase.from("clients").select("id").eq("tenant_id", tenantId).eq("phone", lead.phone).maybeSingle();
        if (data) return (data as { id: string }).id;
      }
    }
    const notes = [
      lead.event_type ? `Event type: ${lead.event_type}` : null,
      lead.event_date ? `Event date: ${fmtDate(lead.event_date)}` : null,
      lead.message    ? `Lead message: ${lead.message}` : null,
    ].filter(Boolean).join("\n");
    const { data: created, error } = await supabase.from("clients").insert({
      tenant_id: tenantId, full_name: lead.name, email: lead.email ?? null, phone: lead.phone ?? null,
      notes: notes || null, status: "active",
    }).select("id").maybeSingle();
    if (error) { Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" }); return null; }
    return (created as { id: string }).id;
  };

  const markBooked = async (leadId: string) => {
    if (!tenantId) return;
    await supabase.from("tenant_public_leads").update({ status: "booked" }).eq("id", leadId).eq("tenant_id", tenantId);
    setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, status: "booked" } : l));
  };

  const handleConvert = async (opts: ConvertOpts) => {
    if (!tenantId || !actionLead) return;
    const clientId = await ensureClient(actionLead, opts.skipIfExists);
    if (!clientId) return;
    await markBooked(actionLead.id);
    logActivity({ tenantId, action: "created", entity: "client", entityId: clientId, entityName: actionLead.name });
    setActionLead(null);
    Swal.fire({ title: "Client created!", text: `"${actionLead.name}" is now in your Clients list.`, icon: "success", timer: 2000, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
  };

  const handleCreateJob = async (opts: JobOpts) => {
    if (!tenantId || !actionLead) return;
    const clientId = await ensureClient(actionLead, true);
    if (!clientId) return;
    const { data: job, error: jobErr } = await supabase.from("jobs").insert({
      tenant_id: tenantId, client_id: clientId, title: opts.title, job_type: opts.job_type,
      status: "scheduled", shoot_date: opts.shoot_date ? new Date(opts.shoot_date).toISOString() : null,
      location: opts.location || null, notes: opts.notes || null,
    }).select("id").maybeSingle();
    if (jobErr || !job) { Swal.fire({ title: "Error", text: jobErr?.message, icon: "error", background: "#ffffff", color: "#0f172a" }); return; }
    const steps = DEFAULT_STEPS.map((s) => ({ ...s, tenant_id: tenantId, job_id: (job as { id: string }).id, status: "pending" }));
    await supabase.from("job_steps").insert(steps);
    await markBooked(actionLead.id);
    logActivity({ tenantId, action: "created", entity: "job", entityId: (job as { id: string }).id, entityName: opts.title });
    setActionLead(null);
    Swal.fire({ title: "Job created!", html: `<p style="color:#64748b;font-size:13px">Job <strong style="color:#9a7840">"${opts.title}"</strong> created.</p>`, icon: "success", timer: 2500, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
  };

  const handleCreateContract = async (opts: ContractOpts) => {
    if (!tenantId || !actionLead) return;
    const clientId = await ensureClient(actionLead, true);
    if (!clientId) return;
    const contractNum = `CTR-${Date.now().toString().slice(-6)}`;
    const balance = opts.total_amount - opts.deposit_amount;
    const { data: contract, error: ctErr } = await supabase.from("contracts").insert({
      tenant_id: tenantId, client_id: clientId, client_name: actionLead.name,
      contract_number: contractNum, title: opts.title,
      event_date: actionLead.event_date ?? "", event_location: opts.event_location || "",
      package_name: opts.package_name || "", total_amount: opts.total_amount,
      deposit_amount: opts.deposit_amount, balance_amount: balance > 0 ? balance : 0,
      status: "draft", payment_status: "unpaid",
    }).select("id").maybeSingle();
    if (ctErr || !contract) { Swal.fire({ title: "Error", text: ctErr?.message, icon: "error", background: "#ffffff", color: "#0f172a" }); return; }
    await markBooked(actionLead.id);
    logActivity({ tenantId, action: "created", entity: "contract", entityId: (contract as { id: string }).id, entityName: opts.title });
    setActionLead(null);
    Swal.fire({ title: "Contract created!", html: `<p style="color:#64748b;font-size:13px">Contract <strong style="color:#9a7840">"${contractNum}"</strong> created as draft.</p>`, icon: "success", timer: 2500, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
  };

  const TABS = [
    { value: "all",       label: "All" },
    { value: "new",       label: "New" },
    { value: "contacted", label: "Contacted" },
    { value: "booked",    label: "Booked" },
    { value: "lost",      label: "Lost" },
  ];

  const hasAnyFilter = search.trim() || advActiveCount > 0 || statusFilter !== "all";

  return (
    <AdminLayout>
      <AdminHeader
        title="Leads"
        subtitle="Manage prospects and track your pipeline"
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCreateOpen(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-add-line"></i>
              </span>
              Add Lead
            </button>
            <button
              onClick={() => setShowDashboard((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                showDashboard
                  ? "bg-[#C8A96E]/10 border-[#C8A96E]/30 text-[#9a7840]"
                  : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-bar-chart-2-line"></i>
              </span>
              {showDashboard ? "Hide" : "Dashboard"}
            </button>
          </div>
        }
      />

      <div className="px-4 md:px-8 py-4 md:py-7">
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={() => tenantId && fetchLeads(tenantId)} showLogin />
        ) : (
          <>
            {/* ── Dashboard (collapsible) ── */}
            {showDashboard && leads.length > 0 && (
              <LeadsDashboard leads={leads} />
            )}

            {/* ── Status tabs + search + filters ── */}
            <div className="flex items-center gap-2 mb-5 flex-wrap">
              {/* Status tabs */}
              <div className="flex items-center gap-0.5 bg-white rounded-xl border border-slate-200 p-1">
                {TABS.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => setStatusFilter(tab.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      statusFilter === tab.value
                        ? "bg-[#C8A96E] text-white"
                        : "text-slate-400 hover:text-slate-700"
                    }`}
                  >
                    {tab.label}
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                      statusFilter === tab.value ? "bg-white/30 text-white" : "bg-slate-100 text-slate-400"
                    }`}>
                      {counts[tab.value] ?? 0}
                    </span>
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="relative flex-1 min-w-[160px] max-w-xs">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                  <i className="ri-search-line text-sm"></i>
                </span>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search leads..."
                  className="w-full bg-white rounded-xl border border-slate-200 pl-9 pr-4 py-2 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 cursor-pointer"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                )}
              </div>

              {/* Advanced filters */}
              <LeadsFilters
                filters={advFilters}
                onChange={setAdvFilters}
                activeCount={advActiveCount}
              />

              {/* Results count + clear */}
              <div className="flex items-center gap-2 ml-auto">
                {hasAnyFilter && (
                  <button
                    onClick={() => { setStatusFilter("all"); setSearch(""); setAdvFilters(EMPTY_FILTERS); }}
                    className="text-slate-400 hover:text-red-500 text-xs flex items-center gap-1 cursor-pointer transition-colors"
                  >
                    <i className="ri-close-line text-sm"></i>
                    Clear
                  </button>
                )}
                <span className="text-slate-400 text-sm whitespace-nowrap">
                  {filtered.length} leads
                </span>
              </div>
            </div>

            {/* ── Active filter chips ── */}
            {advActiveCount > 0 && (
              <div className="flex items-center gap-2 flex-wrap mb-4">
                {advFilters.source && (
                  <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-[#C8A96E]/10 border border-[#C8A96E]/20 text-[#9a7840] text-xs">
                    <i className="ri-global-line text-[10px]"></i>
                    {sourceCfg(advFilters.source).label}
                    <button onClick={() => setAdvFilters((f) => ({ ...f, source: "" }))} className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-[#C8A96E]/20 cursor-pointer">
                      <i className="ri-close-line text-[10px]"></i>
                    </button>
                  </span>
                )}
                {advFilters.event_type && (
                  <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-xs">
                    <i className="ri-calendar-event-line text-[10px]"></i>
                    {advFilters.event_type}
                    <button onClick={() => setAdvFilters((f) => ({ ...f, event_type: "" }))} className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-slate-200 cursor-pointer">
                      <i className="ri-close-line text-[10px]"></i>
                    </button>
                  </span>
                )}
                {(advFilters.date_from || advFilters.date_to) && (
                  <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-xs">
                    <i className="ri-calendar-2-line text-[10px]"></i>
                    {advFilters.date_from && new Date(advFilters.date_from).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    {advFilters.date_from && advFilters.date_to && " — "}
                    {advFilters.date_to && new Date(advFilters.date_to).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    <button onClick={() => setAdvFilters((f) => ({ ...f, date_from: "", date_to: "" }))} className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-slate-200 cursor-pointer">
                      <i className="ri-close-line text-[10px]"></i>
                    </button>
                  </span>
                )}
              </div>
            )}

            {/* ── Empty state (no leads at all) ── */}
            {leads.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 max-w-xs mx-auto text-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-[#C8A96E]/10">
                  <i className="ri-user-received-line text-[#C8A96E] text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-slate-800 font-semibold text-base mb-1.5">No leads yet</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Leads arrive automatically when visitors fill out the contact form on your public studio page.
                  </p>
                </div>
                <button
                  onClick={() => navigate("/admin/website")}
                  className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-global-line"></i>
                  Set Up Public Page
                </button>
              </div>
            ) : filtered.length === 0 ? (
              /* ── Empty filtered state ── */
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-100">
                  <i className="ri-filter-off-line text-slate-400 text-xl"></i>
                </div>
                <p className="text-slate-500 text-sm font-medium">No leads match your filters</p>
                <button
                  onClick={() => { setStatusFilter("all"); setSearch(""); setAdvFilters(EMPTY_FILTERS); }}
                  className="text-[#C8A96E] text-xs hover:underline cursor-pointer flex items-center gap-1"
                >
                  <i className="ri-close-circle-line"></i>
                  Clear all filters
                </button>
              </div>
            ) : (
              /* ── Lead list ── */
              <div className="space-y-2">
                {filtered.map((lead) => {
                  const isExpanded = expandedId === lead.id;
                  const isUpdating = updatingId === lead.id;
                  const cfg        = statusCfg(lead.status);
                  const canAct     = lead.status !== "lost";
                  const sc         = sourceCfg(lead.source ?? null);

                  return (
                    <div
                      key={lead.id}
                      className="bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-all overflow-hidden"
                    >
                      {/* ── Main row ── */}
                      <div className="flex items-center gap-4 px-5 py-4">

                        {/* Avatar */}
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#C8A96E]/10 text-[#9a7840] text-sm font-semibold shrink-0">
                          {lead.name.charAt(0).toUpperCase()}
                        </div>

                        {/* Name + contact */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <p className="text-slate-800 font-medium text-sm truncate">{lead.name}</p>
                            {lead.status === "new" && (
                              <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse"></span>
                            )}
                            <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${sc.cls}`}>
                              <i className={`${sc.icon} text-[9px]`}></i>
                              {sc.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 flex-wrap">
                            {lead.email && (
                              <a href={`mailto:${lead.email}`} className="text-slate-400 text-xs hover:text-slate-600 transition-colors flex items-center gap-1 cursor-pointer">
                                <i className="ri-mail-line text-[10px]"></i>
                                <span className="truncate max-w-[160px]">{lead.email}</span>
                              </a>
                            )}
                            {lead.phone && (
                              <a href={`tel:${lead.phone}`} className="text-slate-400 text-xs hover:text-slate-600 transition-colors flex items-center gap-1 cursor-pointer">
                                <i className="ri-phone-line text-[10px]"></i>
                                {lead.phone}
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Event info */}
                        <div className="hidden lg:block text-right shrink-0">
                          {lead.event_type && <p className="text-slate-500 text-xs">{lead.event_type}</p>}
                          {lead.event_date && <p className="text-slate-400 text-[10px] mt-0.5">{fmtDate(lead.event_date)}</p>}
                          {!lead.event_type && !lead.event_date && <span className="text-slate-300 text-xs">—</span>}
                        </div>

                        {/* Status selector */}
                        <div className="relative shrink-0">
                          <select
                            value={lead.status}
                            onChange={(e) => handleStatus(lead.id, e.target.value)}
                            disabled={isUpdating}
                            className={`appearance-none pl-2.5 pr-7 py-1.5 rounded-full text-xs border font-medium cursor-pointer transition-all outline-none ${cfg.badge} bg-transparent`}
                          >
                            {STATUSES.map((s) => (
                              <option key={s.value} value={s.value} className="bg-white text-slate-800">{s.label}</option>
                            ))}
                          </select>
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-50">
                            {isUpdating
                              ? <div className="w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin"></div>
                              : <i className="ri-arrow-down-s-line text-[10px]"></i>
                            }
                          </span>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-1 shrink-0">
                          {canAct && (
                            <button onClick={() => openAction(lead, "convert")} title="Convert to Client"
                              className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#9a7840] hover:bg-[#C8A96E]/10 rounded-lg transition-all cursor-pointer">
                              <i className="ri-user-add-line text-sm"></i>
                            </button>
                          )}
                          {canAct && (
                            <button onClick={() => openAction(lead, "job")} title="Create Job"
                              className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-violet-500 hover:bg-violet-50 rounded-lg transition-all cursor-pointer">
                              <i className="ri-camera-3-line text-sm"></i>
                            </button>
                          )}
                          {canAct && (
                            <button onClick={() => openAction(lead, "contract")} title="Create Contract"
                              className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 rounded-lg transition-all cursor-pointer">
                              <i className="ri-file-text-line text-sm"></i>
                            </button>
                          )}
                          <button onClick={() => setEditLead(lead)} title="Edit lead"
                            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
                            <i className="ri-pencil-line text-sm"></i>
                          </button>
                          <button onClick={() => handleDelete(lead)} title="Delete lead"
                            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                          <button onClick={() => navigate(`/admin/leads/${lead.id}`)} title="View detail"
                            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#9a7840] hover:bg-[#C8A96E]/10 rounded-lg transition-all cursor-pointer">
                            <i className="ri-arrow-right-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : lead.id)}
                            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
                            <i className={`text-sm ${isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
                          </button>
                        </div>
                      </div>

                      {/* ── Expanded detail ── */}
                      {isExpanded && (
                        <div className="border-t border-slate-100 px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-5 bg-slate-50/50">
                          <div className="md:col-span-2">
                            <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-2">Message</p>
                            {lead.message
                              ? <p className="text-slate-600 text-sm leading-relaxed">{lead.message}</p>
                              : <p className="text-slate-300 text-sm italic">No message provided</p>
                            }
                          </div>
                          <div className="space-y-3">
                            <div>
                              <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">Received</p>
                              <p className="text-slate-500 text-xs">
                                {new Date(lead.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                              </p>
                            </div>
                            <div>
                              <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">Source</p>
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${sc.cls}`}>
                                <i className={`${sc.icon} text-[10px]`}></i>
                                {sc.label}
                              </span>
                            </div>
                            {lead.event_type && (
                              <div>
                                <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">Event</p>
                                <p className="text-slate-500 text-xs">{lead.event_type} {lead.event_date && `· ${fmtDate(lead.event_date)}`}</p>
                              </div>
                            )}
                            <div className="flex items-center gap-2 flex-wrap pt-1">
                              {lead.email && (
                                <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 text-xs transition-all cursor-pointer whitespace-nowrap">
                                  <i className="ri-mail-send-line"></i> Email
                                </a>
                              )}
                              {lead.phone && (
                                <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 text-xs transition-all cursor-pointer whitespace-nowrap">
                                  <i className="ri-phone-line"></i> Call
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {createOpen && <LeadModal onClose={() => setCreateOpen(false)} onSave={handleCreateManual} />}
      {editLead && <LeadModal lead={editLead} onClose={() => setEditLead(null)} onSave={handleSaveEdit} />}
      {actionLead && (
        <LeadActionsModal
          lead={actionLead} mode={actionMode}
          onClose={() => setActionLead(null)}
          onConvert={handleConvert}
          onCreateJob={handleCreateJob}
          onCreateContract={handleCreateContract}
        />
      )}
    </AdminLayout>
  );
}